import {noop} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {openInBrowser} from '../../../../../../../../../srcCommon/browser/openInBrowser';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {ClickEventHandler} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../../../shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';
import {InfoBox} from '../../../common/infobox/infoBox';
import {useCrmPageConfig} from '../crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncsInfoBoxesContainerProps} from './crmSyncsInfoBoxesContainer';
import {CrmSyncsScheduleInfoBox} from './crmSyncsScheduleInfoBox';

/*
 * Styles.
 */

const StyledInfoBoxWrapperDiv = styled.div`
  padding: 15px 53px 24px 0px;
  background: ${(p) => p.theme.foreground};

  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledLearnMoreTitleDiv = styled.div`
  margin-bottom: -10px;
`;

const StyledAdditonalContentDiv = styled.div`
  margin-top: 10px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  font-weight: ${fontWeights.semibold};
  gap: 5px;
`;

const StyledDescriptionDiv = styled.div`
  padding-bottom: 8px;
`;

const StyledButton = styled(Button)`
  margin-left: -14px; // align button text with title
  width: 144px;
`;

/*
 * Component.
 */

export const CrmSyncsInfoBoxes: FC<CrmSyncsInfoBoxesContainerProps> = (props) => {
  const {
    isAdmin,
    source,
    showNextSync,
    nextSyncAt,
    lastSyncedAt,
    jobProgressPercentage,
    hasCrmSyncFeature,
    canSyncNow,
    onSync,
  } = props;
  const {account, card, displayName, infoBoxDescription} = useCrmPageConfig(source);
  const accountHelpCenterLink = account?.helpCenterLink;
  const cardHelpCenterLink = card?.helpCenterLink;
  const hasMultipleSyncs = Boolean(accountHelpCenterLink) && Boolean(cardHelpCenterLink);
  const showSyncScheduleBox = hasCrmSyncFeature && lastSyncedAt !== undefined;

  const maybeRenderInfoBox = () => {
    if (hasMultipleSyncs && showSyncScheduleBox) {
      return null;
    }
    return (
      <InfoBox
        onFooterClick={() => {
          if (accountHelpCenterLink) {
            openInBrowser(accountHelpCenterLink);
          } else if (cardHelpCenterLink) {
            openInBrowser(cardHelpCenterLink);
          }
        }}
        title={renderTitle(showSyncScheduleBox, displayName)}
        footer={maybeRenderFooter(hasMultipleSyncs, showSyncScheduleBox)}
      >
        {renderDescription(showSyncScheduleBox, infoBoxDescription)}
      </InfoBox>
    );
  };

  const maybeRenderMultipleHelpLinksBox = () => {
    if (!accountHelpCenterLink || !cardHelpCenterLink) {
      return null;
    }
    return (
      <InfoBox onFooterClick={noop} title={<Trans id="crm-sync-learn-more-about">Learn more about...</Trans>}>
        {renderInlineButton(
          () => openInBrowser(accountHelpCenterLink),
          <Trans id="crm-sync-account-help-link">Accounts sync</Trans>,
        )}
        {renderInlineButton(
          () => openInBrowser(cardHelpCenterLink),
          <Trans id="crm-sync-contact-help-link">Contacts sync</Trans>,
        )}
      </InfoBox>
    );
  };
  return (
    <StyledInfoBoxWrapperDiv>
      {showSyncScheduleBox && (
        <CrmSyncsScheduleInfoBox
          isAdmin={isAdmin}
          onSync={onSync}
          source={source}
          showNextSync={showNextSync}
          canSyncNow={canSyncNow}
          nextSyncAt={nextSyncAt}
          lastSyncedAt={lastSyncedAt}
          jobProgressPercentage={jobProgressPercentage}
        />
      )}
      {maybeRenderInfoBox()}
      {maybeRenderMultipleHelpLinksBox()}
    </StyledInfoBoxWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderTitle(showSyncScheduleBox: boolean, displayName: string) {
  if (showSyncScheduleBox) {
    return (
      <StyledLearnMoreTitleDiv>
        <Trans id="crm-sync-page-info-box-title-learn-more">Learn more about {displayName} sync</Trans>
      </StyledLearnMoreTitleDiv>
    );
  }
  return <Trans id="crm-sync-page-info-box-title">1-way sync from {displayName} to Front</Trans>;
}

function maybeRenderFooter(hideFooter: boolean, showSyncScheduleBox: boolean) {
  if (hideFooter) {
    return null;
  }

  if (showSyncScheduleBox) {
    return <Trans id="crm-sync-page-infobox-help-center-link">Go to Help Center</Trans>;
  }
  return <Trans id="crm-sync-page-infobox-learn-more-link">Learn more</Trans>;
}

function renderDescription(showSyncScheduleBox: boolean, customDescription?: ReactNode) {
  if (showSyncScheduleBox) {
    return null;
  }
  return (
    <>
      <StyledDescriptionDiv>
        <Trans id="crm-sync-page-infobox-content">
          Automatically syncs once per day. You can also manually trigger a sync at any time from this page.
        </Trans>
      </StyledDescriptionDiv>
      {customDescription && <StyledAdditonalContentDiv>{customDescription}</StyledAdditonalContentDiv>}
    </>
  );
}

function renderInlineButton(onClick: ClickEventHandler, content: ReactNode) {
  return (
    <StyledButton size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.LINK} onClick={onClick}>
      <StyledButtonContentDiv>
        {content}
        <Icon name="externalLinkSmall" />
      </StyledButtonContentDiv>
    </StyledButton>
  );
}
